// frontend/src/components/Sidebar.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
    FaBars,
    FaMinus,
    FaTachometerAlt,
    FaWhatsapp,
    FaChevronDown,
    FaChevronRight,
    FaComments,
    FaTicketAlt,
    FaUsers,
    FaList,
    FaCog,
    FaMoon,
    FaSun,
    FaSignOutAlt,
    FaPeopleArrows,
    FaHome,
    FaChartBar,
    FaFileAlt,
    FaBullhorn,
    FaClock,
    FaServer,
    FaMoneyBillWave,
    FaProjectDiagram,
    FaMicrophone,
    FaBitcoin,
    FaBrain,
    FaLightbulb,
    FaRobot,
    FaClipboardCheck,
    FaFileInvoiceDollar,
    FaHandHoldingUsd,
    FaCreditCard,
    FaFlask,
    FaLandmark,
    FaYoutube
} from 'react-icons/fa';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { ThemeContext } from '../contexts/ThemeContext';
import { useAuthContext } from '../contexts/AuthContext';
import operationsService from '../services/operationsService';
import vortiqSymbol from '../assets/logoV.png';
import vortiqHorizontal from '../assets/logofull.png';
import './Sidebar.css';

const Sidebar = ({ user: userProp, onLogout, isCollapsed, onToggle }) => {
    const navigate = useNavigate();
    const { theme, toggleTheme } = useContext(ThemeContext);
    const { user, handleLogout } = useAuthContext();
    const authSeed = userProp || user || null;
    const currentUser = (authSeed && typeof authSeed === 'object'
        ? (authSeed.user || authSeed.userData || authSeed.account || authSeed)
        : authSeed) || null;
    const isIaStudioAdmin = useMemo(() => {
        const role = String(currentUser?.role || currentUser?.userRole || '').trim().toLowerCase();
        const profile = String(currentUser?.profile || currentUser?.type || '').trim().toLowerCase();
        const roles = Array.isArray(currentUser?.roles) ? currentUser.roles.map((r) => String(r || '').trim().toLowerCase()) : [];
        const permissions = Array.isArray(currentUser?.permissions)
            ? currentUser.permissions.map((p) => String(p || '').trim().toLowerCase())
            : [];
        const flag = currentUser?.isAdmin === true || currentUser?.admin === true;
        const allow = ['super_admin', 'admin', 'manager', 'supervisor'];
        return flag
            || allow.includes(role)
            || allow.includes(profile)
            || roles.some((r) => allow.includes(r))
            || permissions.some((p) => allow.includes(p) || p.includes('admin') || p.includes('settings') || p.includes('youtube'));
    }, [currentUser?.role, currentUser?.userRole, currentUser?.profile, currentUser?.type, currentUser?.roles, currentUser?.permissions, currentUser?.isAdmin, currentUser?.admin]);
    const [isWhatsAppExpanded, setIsWhatsAppExpanded] = useState(false);
    const [isIaStudioExpanded, setIsIaStudioExpanded] = useState(false);
    const [isSmsExpanded, setIsSmsExpanded] = useState(false);
    const [isProtheusExpanded, setIsProtheusExpanded] = useState(false);
    const [isCriptoExpanded, setIsCriptoExpanded] = useState(false);
    const location = useLocation();
    const [presenceStatus, setPresenceStatus] = useState('online');
    const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
    const [pauseTypes, setPauseTypes] = useState([]);
    const [selectedPauseTypeId, setSelectedPauseTypeId] = useState('');
    const [pauseNote, setPauseNote] = useState('');
    const [currentPause, setCurrentPause] = useState(null);
    const [pauseElapsedSeconds, setPauseElapsedSeconds] = useState(0);

    React.useEffect(() => {
        const whatsappPaths = new Set([
            '/tickets',
            '/contacts',
            '/connections',
            '/integrated-whatsapp-chat',
            '/whatsapp-connections',
            '/queues',
            '/settings',
            '/reports',
            '/operations',
            '/sla',
            '/campaigns',
            '/automation',
            '/external-api-integrations',
            '/external-message-templates',
            '/external-api-sends',
            '/external-api-webhooks',
            '/pause-types',
            '/omni-dashboard'
        ]);

        const iaStudioPaths = new Set([
            '/content',
            '/youtube-scripts',
            '/ai-settings',
            '/voice-ai',
            '/image-ai',
            '/videos',
            '/templates',
            '/video-editor',
            '/youtube',
            '/youtube-analytics',
            '/youtube-automation',
            '/production',
            '/production-dashboard',
            '/design-system'
        ]);

        const smsPaths = new Set([
            '/sms-gateways'
        ]);
        const path = String(location.pathname || '');
        if (iaStudioPaths.has(path) || path.startsWith('/video-editor/') || path.startsWith('/production/')) {
            setIsIaStudioExpanded(true);
        }
        if (smsPaths.has(path)) {
            setIsSmsExpanded(true);
        }
        if (path.startsWith('/cripto')) {
            setIsCriptoExpanded(true);
        }
    }, [location.pathname]);

    React.useEffect(() => {
        if (String(location.pathname || '').startsWith('/protheus')) {
            setIsProtheusExpanded(true);
        }
    }, [location.pathname]);

    const pauseTypeById = useMemo(() => new Map((pauseTypes || []).map(p => [String(p.id), p])), [pauseTypes]);

    useEffect(() => {
        const load = async () => {
            try {
                const [typesResp, currentResp, presenceResp] = await Promise.all([
                    operationsService.listPauseReasons(),
                    operationsService.getMyCurrentPause(),
                    operationsService.getMyPresence()
                ]);
                const types = typesResp?.data?.reasons || typesResp?.reasons || [];
                setPauseTypes(Array.isArray(types) ? types : []);
                const pause = currentResp?.data?.pause ?? currentResp?.pause ?? null;
                setCurrentPause(pause);
                const presence = presenceResp?.data?.presence || presenceResp?.presence || null;
                if (presence?.status) setPresenceStatus(String(presence.status));

                if (pause?.startedAt) {
                    const started = new Date(pause.startedAt).getTime();
                    const now = Date.now();
                    setPauseElapsedSeconds(Math.max(0, Math.floor((now - started) / 1000)));
                    setPresenceStatus('paused');
                } else {
                    setPauseElapsedSeconds(0);
                }
            } catch {
            }
        };
        load();
    }, []);

    useEffect(() => {
        if (!currentPause?.startedAt || currentPause?.endedAt) return;
        const interval = setInterval(() => {
            const started = new Date(currentPause.startedAt).getTime();
            const now = Date.now();
            setPauseElapsedSeconds(Math.max(0, Math.floor((now - started) / 1000)));
        }, 1000);
        return () => clearInterval(interval);
    }, [currentPause?.startedAt, currentPause?.endedAt]);

    const handleLogoutClick = () => {
        if (typeof onLogout === 'function') {
            onLogout();
        } else {
            handleLogout();
        }
        navigate('/login');
    };

    // Função para lidar com o clique no menu WhatsApp
    const handleWhatsAppClick = (e) => {
        e.preventDefault();
        setIsWhatsAppExpanded(!isWhatsAppExpanded);

        // Notificar mudança de contexto
        if (window.sessionStorage) {
            window.sessionStorage.setItem('lastActiveModule', 'whatsapp');
        }
    };

    const handleProtheusClick = (e) => {
        e.preventDefault();
        setIsProtheusExpanded(!isProtheusExpanded);
        if (window.sessionStorage) {
            window.sessionStorage.setItem('lastActiveModule', 'protheus');
        }
    };

    const handleIaStudioClick = (e) => {
        e.preventDefault();
        setIsIaStudioExpanded(!isIaStudioExpanded);
        if (window.sessionStorage) {
            window.sessionStorage.setItem('lastActiveModule', 'ia-studio');
        }
    };

    const handleSmsClick = (e) => {
        e.preventDefault();
        setIsSmsExpanded(!isSmsExpanded);
        if (window.sessionStorage) {
            window.sessionStorage.setItem('lastActiveModule', 'sms');
        }
    };

    const handleCriptoClick = (e) => {
        e.preventDefault();
        setIsCriptoExpanded(!isCriptoExpanded);
        if (window.sessionStorage) {
            window.sessionStorage.setItem('lastActiveModule', 'cripto');
        }
    };

    const formattedPauseTime = (() => {
        const s = Number(pauseElapsedSeconds) || 0;
        const hh = String(Math.floor(s / 3600)).padStart(2, '0');
        const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
        const ss = String(s % 60).padStart(2, '0');
        return `${hh}:${mm}:${ss}`;
    })();

    const openPauseDialog = () => {
        setSelectedPauseTypeId('');
        setPauseNote('');
        setPauseDialogOpen(true);
    };

    const closePauseDialog = () => {
        setPauseDialogOpen(false);
        setSelectedPauseTypeId('');
        setPauseNote('');
    };

    const refreshPauseState = async () => {
        const currentResp = await operationsService.getMyCurrentPause();
        const pause = currentResp?.data?.pause ?? currentResp?.pause ?? null;
        setCurrentPause(pause);
        if (pause?.startedAt) setPresenceStatus('paused');
        else setPresenceStatus('online');
    };

    const startPause = async () => {
        const id = Number(selectedPauseTypeId);
        if (!id) return;
        const type = pauseTypeById.get(String(selectedPauseTypeId)) || null;
        if (type?.requiresNote && !String(pauseNote || '').trim()) {
            toast.error('Este motivo exige observação');
            return;
        }
        try {
            const presenceResp = await operationsService.getMyPresence().catch(() => null);
            const currentStatus = presenceResp?.data?.presence?.status || presenceResp?.presence?.status || presenceStatus;
            if (String(currentStatus || '') === 'offline') {
                await operationsService.setMyPresence('online');
            }
            await operationsService.startMyPause(id, pauseNote || null);
            closePauseDialog();
            await refreshPauseState();
        } catch (e) {
            toast.error(e?.message || 'Falha ao iniciar pausa');
            await refreshPauseState().catch(() => {});
        }
    };

    const endPause = async () => {
        try {
            await operationsService.endMyPause();
            await refreshPauseState();
        } catch (e) {
            toast.error(e?.message || 'Falha ao encerrar pausa');
        }
    };

    return (
        <>
            <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
                <div>
                    <div className="sidebar-header">
                        <div className="sidebar-brand" title="VORTIQA">
                            {isCollapsed ? (
                                <img className="sidebar-brand-symbol" src={vortiqSymbol} alt="VORTIQA" />
                            ) : (
                                <img className="sidebar-brand-horizontal" src={vortiqHorizontal} alt="VORTIQA" />
                            )}
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        <ul>
                            <li className="operator-card">
                                <div className="operator-card-inner">
                                    <div className={`status-indicator ${presenceStatus === 'paused' ? 'paused' : presenceStatus === 'offline' ? 'offline' : 'online'}`}></div>
                                    <div className="operator-card-body">
                                        <div className="operator-card-name">
                                            {!isCollapsed && (
                                                userProp?.nome || user?.nome ||
                                                userProp?.name || user?.name ||
                                                userProp?.usuario || user?.usuario ||
                                                userProp?.fullName || user?.fullName ||
                                                userProp?.user?.nome || user?.user?.nome ||
                                                userProp?.user?.name || user?.user?.name ||
                                                userProp?.user?.usuario || user?.user?.usuario ||
                                                'Operador'
                                            )}
                                        </div>
                                        {!isCollapsed && (
                                            <div className="operator-card-meta">
                                                <Chip size="small" label={presenceStatus === 'paused' ? 'Em pausa' : presenceStatus === 'offline' ? 'Offline' : 'Online'} color={presenceStatus === 'paused' ? 'warning' : presenceStatus === 'offline' ? 'default' : 'success'} />
                                                {presenceStatus === 'paused' && (
                                                    <span className="operator-card-pause">
                                                        {(currentPause?.type?.name) || (pauseTypeById.get(String(currentPause?.pauseTypeId || currentPause?.pauseReasonId || ''))?.name) || 'Pausa'} · {formattedPauseTime}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {!isCollapsed && (
                                        <div className="operator-card-actions">
                                            {presenceStatus === 'paused' ? (
                                                <Button size="small" variant="contained" onClick={endPause}>Voltar</Button>
                                            ) : presenceStatus === 'offline' ? (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={async () => {
                                                        try {
                                                            await operationsService.setMyPresence('online');
                                                            await refreshPauseState();
                                                        } catch (e) {
                                                            toast.error(e?.message || 'Falha ao iniciar');
                                                        }
                                                    }}
                                                >
                                                    Iniciar
                                                </Button>
                                            ) : (
                                                <Button size="small" variant="outlined" onClick={openPauseDialog}>Pausar</Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </li>
                            <li>
                                <NavLink to="/dashboard">
                                    <FaTachometerAlt />
                                    <span>{!isCollapsed && 'Dashboard'}</span>
                                </NavLink>
                            </li>
                            <li className="whatsapp-menu">
                                <div className="menu-item-with-submenu" onClick={handleWhatsAppClick}>
                                    <div className="menu-item-content">
                                        <FaWhatsapp />
                                        <span>{!isCollapsed && 'WhatsApp'}</span>
                                    </div>
                                    {!isCollapsed && (
                                        <div className="submenu-toggle">
                                            {isWhatsAppExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                        </div>
                                    )}
                                </div>
                                {isWhatsAppExpanded && !isCollapsed && (
                                    <ul className="submenu">
                                        <li>
                                            <NavLink to="/dashboard">
                                                <FaHome />
                                                <span>Dashboard</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/operations">
                                                <FaChartBar />
                                                <span>Operação</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/sla">
                                                <FaClock />
                                                <span>SLA</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/reports">
                                                <FaFileAlt />
                                                <span>Relatórios</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/tickets">
                                                <FaTicketAlt />
                                                <span>Tickets</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/contacts">
                                                <FaPeopleArrows />
                                                <span>Contatos</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/integrated-whatsapp-chat">
                                                <FaComments />
                                                <span>Chats</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/whatsapp-connections">
                                                <FaCog />
                                                <span>Conexões WhatsApp</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/queues">
                                                <FaList />
                                                <span>Filas</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/campaigns">
                                                <FaBullhorn />
                                                <span>Campanhas</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/automation">
                                                <FaProjectDiagram />
                                                <span>Automação</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/settings">
                                                <FaCog />
                                                <span>Configurações</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/pause-types">
                                                <FaCog />
                                                <span>Tipos de pausa</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/external-api-integrations">
                                                <FaCog />
                                                <span>Integrações ERP</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/external-message-templates">
                                                <FaFileAlt />
                                                <span>Mensagens ERP</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/external-api-sends">
                                                <FaFileAlt />
                                                <span>Envios ERP</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/external-api-webhooks">
                                                <FaFileAlt />
                                                <span>Webhooks ERP</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/external-api-queue">
                                                <FaChartBar />
                                                <span>Monitoramento ERP</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/omni-dashboard">
                                                <FaChartBar />
                                                <span>Omnichannel</span>
                                            </NavLink>
                                        </li>
                                    </ul>
                                )}
                            </li>
                            <li className="whatsapp-menu">
                                <div className="menu-item-with-submenu" onClick={handleCriptoClick}>
                                    <div className="menu-item-content">
                                        <FaBitcoin />
                                        <span>{!isCollapsed && 'Cripto'}</span>
                                    </div>
                                    {!isCollapsed && (
                                        <div className="submenu-toggle">
                                            {isCriptoExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                        </div>
                                    )}
                                </div>
                                {isCriptoExpanded && !isCollapsed && (
                                    <ul className="submenu">
                                        <li>
                                            <NavLink to="/cripto/dashboard">
                                                <FaHome />
                                                <span>Dashboard</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/contas">
                                                <FaLandmark />
                                                <span>Contas</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/ordens">
                                                <FaMoneyBillWave />
                                                <span>Ordens Automáticas</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/monitoramento-saldos">
                                                <FaChartBar />
                                                <span>Monitoramento Saldos</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/monitoramento-moedas">
                                                <FaChartBar />
                                                <span>Monitoramento Moedas</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/monitoramento-alertas">
                                                <FaBullhorn />
                                                <span>Monitoramento Alertas</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/analise-ia">
                                                <FaBrain />
                                                <span>Análise IA</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/sugestoes-ia">
                                                <FaLightbulb />
                                                <span>Sugestões IA</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/performance-ia">
                                                <FaRobot />
                                                <span>Performance IA</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/auditoria-ia">
                                                <FaClipboardCheck />
                                                <span>Auditoria IA</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/estrategias">
                                                <FaProjectDiagram />
                                                <span>Estratégias</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/alertas">
                                                <FaBullhorn />
                                                <span>Alertas</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/gestao-financeira">
                                                <FaFileAlt />
                                                <span>Gestão Financeira</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/financas-pessoais">
                                                <FaFileAlt />
                                                <span>Finanças Pessoais</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/renda-passiva">
                                                <FaHandHoldingUsd />
                                                <span>Renda Passiva</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/relatorio-ia">
                                                <FaClipboardCheck />
                                                <span>Relatório IA</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/relatorio-ganhos-perdas">
                                                <FaClipboardCheck />
                                                <span>Ganhos e Perdas</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/relatorio-fluxo-caixa">
                                                <FaFileInvoiceDollar />
                                                <span>Fluxo de Caixa</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/relatorio-fiscal">
                                                <FaFileInvoiceDollar />
                                                <span>Eficiência Fiscal</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/auto-mode">
                                                <FaRobot />
                                                <span>Modo Automático</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/validacao">
                                                <FaFlask />
                                                <span>IA vs Manual</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/configuracoes">
                                                <FaCog />
                                                <span>Configurações</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/configuracoes/ia">
                                                <FaRobot />
                                                <span>Configuração IA</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/configuracoes/moedas-principais">
                                                <FaChartBar />
                                                <span>Moedas Principais</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/assinatura">
                                                <FaCreditCard />
                                                <span>Assinatura</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/admin/users">
                                                <FaUsers />
                                                <span>Admin · Usuários</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/cripto/admin/feedbacks">
                                                <FaFileAlt />
                                                <span>Admin · Feedbacks</span>
                                            </NavLink>
                                        </li>
                                    </ul>
                                )}
                            </li>
                            <li className="whatsapp-menu">
                                <div className="menu-item-with-submenu" onClick={handleIaStudioClick}>
                                    <div className="menu-item-content">
                                        <FaProjectDiagram />
                                        <span>{!isCollapsed && 'IA Studio'}</span>
                                    </div>
                                    {!isCollapsed && (
                                        <div className="submenu-toggle">
                                            {isIaStudioExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                        </div>
                                    )}
                                </div>
                                {isIaStudioExpanded && !isCollapsed && (
                                    <ul className="submenu">
                                        <li>
                                            <NavLink to="/production">
                                                <FaProjectDiagram />
                                                <span>Projetos</span>
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink to="/production-dashboard">
                                                <FaTachometerAlt />
                                                <span>Dashboard</span>
                                            </NavLink>
                                        </li>
                                        {isIaStudioAdmin && (
                                            <>
                                                <li>
                                                    <NavLink to="/ai-settings">
                                                        <FaCog />
                                                        <span>Configurações IA</span>
                                                    </NavLink>
                                                </li>
                                                <li>
                                                    <NavLink to="/youtube">
                                                        <FaYoutube />
                                                        <span>YouTube</span>
                                                    </NavLink>
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                )}
                            </li>
                            <li className="whatsapp-menu">
                                <div className="menu-item-with-submenu" onClick={handleSmsClick}>
                                    <div className="menu-item-content">
                                        <FaServer />
                                        <span>{!isCollapsed && 'SMS'}</span>
                                    </div>
                                    {!isCollapsed && (
                                        <div className="submenu-toggle">
                                            {isSmsExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                        </div>
                                    )}
                                </div>
                                {isSmsExpanded && !isCollapsed && (
                                    <ul className="submenu">
                                        <li>
                                            <NavLink to="/sms-gateways">
                                                <FaServer />
                                                <span>Dispositivos SMS</span>
                                            </NavLink>
                                        </li>
                                    </ul>
                                )}
                            </li>
                            <li className="whatsapp-menu">
                                <div className="menu-item-with-submenu" onClick={handleProtheusClick}>
                                    <div className="menu-item-content">
                                        <FaServer />
                                        <span>{!isCollapsed && 'Protheus'}</span>
                                    </div>
                                    {!isCollapsed && (
                                        <div className="submenu-toggle">
                                            {isProtheusExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                        </div>
                                    )}
                                </div>
                                {isProtheusExpanded && !isCollapsed && (
                                    <ul className="submenu">
                                        <li>
                                            <NavLink to="/protheus/pagamentos">
                                                <FaMoneyBillWave />
                                                <span>Pagamentos</span>
                                            </NavLink>
                                        </li>
                                    </ul>
                                )}
                            </li>
                            <li>
                                <NavLink to="/users">
                                    <FaUsers />
                                    <span>{!isCollapsed && 'Usuários'}</span>
                                </NavLink>
                            </li>
                        </ul>
                    </nav>
                </div>
                <div className="sidebar-footer">
                    <button onClick={toggleTheme} className="theme-toggle">
                        {theme === 'light' ? <FaMoon /> : <FaSun />}
                        <span>{!isCollapsed && `Modo ${theme === 'light' ? 'Escuro' : 'Claro'}`}</span>
                    </button>
                    <a href="#!" onClick={handleLogoutClick} className="logout-link">
                        <FaSignOutAlt /><span>{!isCollapsed && 'Sair'}</span>
                    </a>
                    <button className="sidebar-collapse-btn" onClick={onToggle}>
                        {isCollapsed ? <FaBars /> : <FaMinus />}
                        <span>{!isCollapsed && 'Recolher'}</span>
                    </button>
                </div>
            </div>

            <Dialog open={pauseDialogOpen} onClose={closePauseDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Iniciar pausa</DialogTitle>
                <DialogContent>
                    <Box mt={1} display="grid" gap={2}>
                        <FormControl fullWidth>
                            <InputLabel>Motivo</InputLabel>
                            <Select
                                label="Motivo"
                                value={selectedPauseTypeId}
                                onChange={(e) => setSelectedPauseTypeId(e.target.value)}
                            >
                                {pauseTypes.map((p) => (
                                    <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {(() => {
                            const type = pauseTypeById.get(String(selectedPauseTypeId)) || null;
                            if (!type?.requiresNote) return null;
                            return (
                                <TextField
                                    label="Observação"
                                    value={pauseNote}
                                    onChange={(e) => setPauseNote(e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                />
                            );
                        })()}
                        <Typography variant="body2" color="text.secondary">
                            A pausa será registrada e refletirá no status operacional.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={closePauseDialog}>Cancelar</Button>
                    <Button variant="contained" onClick={startPause} disabled={!selectedPauseTypeId}>Confirmar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Sidebar;


